<template>
  <b-modal
    :model-value="modelValue"
    title="Generate Berita Acara"
    title-class="text-black font-18"
    body-class="p-4"
    hide-footer
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <!-- ── Loading state saat fetch data validator ───────────────── -->
    <div v-if="loadingValidator" class="text-center py-4">
      <i class="fas fa-spinner fa-spin fa-2x text-secondary"></i>
      <p class="mt-2 text-muted mb-0">Memuat data validator...</p>
    </div>

    <!-- ── Form utama ─────────────────────────────────────────────── -->
    <form v-else @submit.prevent="handleSubmit">
      <!-- ─ SECTION: Data Utama ──────────────────────────────────── -->
      <SectionHeader icon="fa-user" label="Data Utama" />

      <div class="row">
        <div class="col-md-6 form-group mb-3">
          <label>Nama <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nama"
            placeholder="Nama lengkap"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Induk Pegawai <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nip"
            placeholder="NIP"
          />
        </div>

        <div class="col-md-12 form-group mb-3">
          <label>Alamat Lengkap <span class="text-danger">*</span></label>
          <textarea
            class="form-control"
            rows="2"
            v-model="form.alamat"
            placeholder="Alamat lengkap"
          ></textarea>
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Unit Kerja <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.unit_kerja"
            placeholder="Unit kerja"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Jabatan <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.jabatan"
            placeholder="Jabatan"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Telepon <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nomor_telepon"
            placeholder="Nomor telepon"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nama Bank <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nama_bank"
            placeholder="Nama bank"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Rekening <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nomor_rekening"
            placeholder="Nomor rekening"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Atas Nama Rekening <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.atas_nama_rekening"
            placeholder="Atas nama rekening"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>NIK <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nik"
            placeholder="Nomor Induk Kependudukan"
            maxlength="16"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>NPWP <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.npwp"
            placeholder="Nomor Pokok Wajib Pajak"
          />
        </div>
      </div>

      <!-- ─ SECTION: Upload Dokumen ──────────────────────────────── -->
      <SectionHeader icon="fa-paperclip" label="Upload Dokumen" />

      <div class="row">
        <div class="col-md-6 form-group mb-3">
          <label>Upload KTP</label>
          <input
            type="file"
            class="form-control"
            accept="image/*,.pdf"
            @change="(e) => (form.upload_ktp = e.target.files[0] ?? null)"
          />
          <small class="text-muted">Format: JPG, PNG, PDF. Maks 2MB.</small>
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Upload NPWP</label>
          <input
            type="file"
            class="form-control"
            accept="image/*,.pdf"
            @change="(e) => (form.upload_npwp = e.target.files[0] ?? null)"
          />
          <small class="text-muted">Format: JPG, PNG, PDF. Maks 2MB.</small>
        </div>
      </div>

      <!-- ─ SECTION: Kritik & Saran ───────────────────────────────── -->
      <SectionHeader icon="fa-comment-dots" label="Kritik & Saran" />

      <div class="form-group mb-4">
        <textarea
          class="form-control"
          rows="4"
          v-model="form.kritik_saran"
          placeholder="Tuliskan kritik atau saran Anda di sini (opsional)"
        ></textarea>
        <small class="text-muted">Tidak wajib diisi.</small>
      </div>

      <!-- ─ SECTION: Tanda Tangan ────────────────────────────────── -->
      <SectionHeader icon="fa-pen-nib" label="Tanda Tangan" required />

      <div class="form-group mb-4">
        <SignaturePad
          ref="signaturePadRef"
          :reset-trigger="signatureResetTrigger"
          :initial-image="form.tanda_tangan"
          @update="(val) => (form.tanda_tangan = val)"
        />
      </div>

      <!-- ─ Action Buttons ───────────────────────────────────────── -->
      <div class="text-end border-top pt-3">
        <button
          type="button"
          class="btn btn-light me-2"
          @click="$emit('update:modelValue', false)"
        >
          Tutup
        </button>
        <button type="submit" class="btn btn-success">
          <i class="fas fa-file-contract me-1"></i> Generate Berita Acara
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import Swal from "sweetalert2";
import SignaturePad from "./SignaturePad.vue";
import SectionHeader from "./SectionHeader.vue";
import { useBeritaAcara } from "./useBeritaAcara.js";

export default {
  name: "ModalBeritaAcara",

  components: {
    SignaturePad,
    SectionHeader,
  },

  props: {
    // v-model — kontrol visibilitas modal dari parent
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Row data yang dipilih dari tabel (harus punya idverif_kompetensi_level)
    rowData: {
      type: Object,
      default: null,
    },
  },

  emits: [
    "update:modelValue", // v-model
    "success", // dipancarkan setelah submit berhasil → parent refresh tabel
  ],

  setup() {
    const {
      form,
      loadingValidator,
      resetForm,
      getValidatorData,
      validateForm,
      submitBeritaAcara,
    } = useBeritaAcara();

    return {
      form,
      loadingValidator,
      resetForm,
      getValidatorData,
      validateForm,
      submitBeritaAcara,
    };
  },

  data() {
    return {
      // Increment untuk trigger reset canvas SignaturePad
      signatureResetTrigger: 0,
    };
  },

  methods: {
    // ── Dipanggil saat modal selesai muncul ──────────────────────
    onModalShown() {
      this.resetForm();
      this.signatureResetTrigger++; // reset canvas tanda tangan
      this.getValidatorData();
    },

    // ── Dipanggil saat modal ditutup ─────────────────────────────
    onModalHidden() {
      this.resetForm();
      this.signatureResetTrigger++;
    },

    // ── Handler submit form ──────────────────────────────────────
    async handleSubmit() {
      // Cek apakah canvas tanda tangan kosong melalui ref SignaturePad
      const isBlank = this.$refs.signaturePadRef?.isBlank() ?? true;

      const errors = this.validateForm(isBlank);

      if (errors.length) {
        Swal.fire({
          icon: "error",
          title: "Validasi Gagal",
          html: `<ul style="text-align:left;">${errors
            .map((e) => `<li>${e}</li>`)
            .join("")}</ul>`,
        });
        return;
      }

      const confirmed = await Swal.fire({
        title: "Apakah Anda yakin?",
        text: "Berita Acara akan di-generate.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Ya, Lanjutkan!",
        cancelButtonText: "Batalkan",
      });

      if (!confirmed.isConfirmed) return;

      await this.submitBeritaAcara(this.rowData?.idverif_kompetensi_level, {
        onSuccess: () => {
          this.$emit("update:modelValue", false);
          this.$emit("success"); // parent akan reload tabel
        },
      });
    },
  },
};
</script>
